import React, { FunctionComponent } from "react";

import cls from "classnames";
import moment from "moment";
import { Tag } from "antd";

import { IProgressAuditTrace } from "../../../features/progressAudit/progressAuditTracesSlice";
import { CogIcon } from "../../common/CogIcon";
import { copyToClipboard } from "../../utils/clipboard";
import { EXIT_POINT_LABELS, BRANCH_LABELS, FLAG_LABELS, formatDurationMs } from "./traceLabels";
import { getSnapshotDiffRows } from "./traceDetailUtils";

import styles from "./progressAudit.module.scss";

const STATUS_LABELS: Record<string, string> = {
  started: "Iniciada",
  completed: "Completada",
  failed: "Falló",
};

const STATUS_COLORS: Record<string, string> = {
  started: "orange",
  completed: "green",
  failed: "red",
};

interface TraceDetailPanelProps {
  trace: IProgressAuditTrace;
}

export const TraceDetailPanel: FunctionComponent<TraceDetailPanelProps> = ({ trace }) => {
  const diffRows = getSnapshotDiffRows(trace.before, trace.after);
  const flagEntries: { label: string; value: boolean }[] = [
    { label: "Con autorización", value: trace.hasAuthorization },
    ...(Object.keys(FLAG_LABELS) as (keyof typeof FLAG_LABELS)[]).map((key) => ({
      label: FLAG_LABELS[key],
      value: !!trace.flags?.[key],
    })),
  ];

  return (
    <div className={styles.traceDetailPanel}>
      <div className={styles.traceDetailSection}>
        <div className={styles.traceDetailHeaderRow}>
          <span className={styles.traceDetailExitPoint}>
            {trace.exitPoint ? EXIT_POINT_LABELS[trace.exitPoint] : "En curso…"}
          </span>
          <span className={styles.traceDetailHeaderTags}>
            {trace.branch && <Tag>{BRANCH_LABELS[trace.branch]}</Tag>}
            <Tag color={STATUS_COLORS[trace.status]}>{STATUS_LABELS[trace.status] || trace.status}</Tag>
            <span
              className={styles.traceDetailCopyButton}
              title="Copiar traza completa como JSON"
              onClick={() => copyToClipboard(JSON.stringify(trace, null, 2))}
            >
              <CogIcon size={14} icon="Copy" />
            </span>
          </span>
        </div>
      </div>

      <div className={styles.traceDetailSection}>
        <div className={styles.traceDetailSectionTitle}>Progress Document ID</div>
        <div className={styles.traceDetailMono}>{trace.progressDocumentId || "—"}</div>
      </div>

      <div className={styles.traceDetailSection}>
        <div className={styles.traceDetailSectionTitle}>Payload</div>
        <pre className={styles.traceDetailPre}>{JSON.stringify(trace.payload, null, 2)}</pre>
      </div>

      <div className={styles.traceDetailSection}>
        <div className={styles.traceDetailSectionTitle}>Antes / Después</div>
        {diffRows.length === 0 ? (
          <div className={styles.traceDetailEmpty}>Sin datos de antes/después para esta traza.</div>
        ) : (
          <div className={styles.traceDetailTableWrapper}>
            <table className={styles.traceDetailTable}>
              <thead>
                <tr>
                  <th>Campo</th>
                  <th>Antes</th>
                  <th>Después</th>
                </tr>
              </thead>
              <tbody>
                {diffRows.map((row) => (
                  <tr key={row.key} className={cls({ [styles.traceDetailRowDiff]: row.isDifferent })}>
                    <td>{row.key}</td>
                    <td>{row.before}</td>
                    <td>{row.after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.traceDetailSection}>
        <div className={styles.traceDetailSectionTitle}>Tiempos</div>
        <div className={styles.traceDetailTimes}>
          <div>Inicio: {moment(trace.receivedAt).format("DD/MM/YYYY HH:mm:ss")}</div>
          <div>Fin: {trace.finishedAt ? moment(trace.finishedAt).format("DD/MM/YYYY HH:mm:ss") : "—"}</div>
          <div>Duración: {formatDurationMs(trace.durationMs) || "—"}</div>
        </div>
      </div>

      <div className={styles.traceDetailSection}>
        <div className={styles.traceDetailSectionTitle}>Flags</div>
        <div className={styles.traceDetailFlags}>
          {flagEntries.map(({ label, value }) => (
            <span
              key={label}
              className={cls(styles.traceDetailFlag, {
                [styles.traceDetailFlagOn]: value,
                [styles.traceDetailFlagOff]: !value,
              })}
            >
              <span className={styles.traceDetailFlagDot} />
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
